<template>
  <div class="h-full w-full p-4">
    <FileUpload
      ref="fileUploadRef"
      :accept="ACCEPT_STRING"
      multiple
      custom-upload
      :pt="{
        root: { class: 'h-full w-full !bg-surface-50 !text-surface-950' },
        input: { class: 'bg-surface-100 border-surface-300 text-surface-950' },
        buttonbar: { class: 'bg-surface-100 border-surface-300' },
        chooseButton: {
          class:
            'bg-surface-200 border-surface-300 hover:bg-surface-300 text-surface-950',
        },
        uploadButton: {
          class: 'stw-brand-gradient border-transparent hover:brightness-110',
        },
        cancelButton: {
          class:
            'bg-surface-200 border-surface-300 hover:bg-surface-300 text-surface-950',
        },
      }"
      @select="handleSelect"
      @upload="handleUpload"
    >
      <template #header>
        <div />
      </template>

      <template
        #content="{ files, uploadedFiles, removeFileCallback, messages }"
      >
        <div
          :class="[
            'flex flex-col gap-8 pt-4',
            {
              'flex-1':
                files.length > 0 ||
                uploadedFiles.length > 0 ||
                (messages && messages.length > 0),
            },
          ]"
        >
          <StwMessage
            v-for="message of messages"
            :key="message"
            :class="{ 'mb-8': !files.length && !uploadedFiles.length }"
            severity="error"
          >
            {{ message }}
          </StwMessage>

          <div v-if="files.length > 0" class="flex w-full justify-center">
            <div class="w-full max-w-md text-center">
              <div class="!text-surface-950 mb-4 text-lg font-bold">
                Selected File(s)
              </div>

              <div
                class="bg-surface-50/50 !text-surface-950 rounded-xl p-6 shadow-sm ring-1 ring-black/10 backdrop-blur"
              >
                <template v-if="files.length === 1">
                  <div class="mb-2 break-all text-xl font-semibold">
                    {{ files[0].name }}
                  </div>
                  <div class="mb-2 text-sm opacity-80">
                    Type: {{ getFileTypeString(files[0]) }}
                  </div>
                  <div class="mb-4 text-sm opacity-80">
                    Size: {{ getFileSizeString(files[0].size) }}
                  </div>
                </template>

                <template v-else>
                  <div class="mb-4 max-h-40 overflow-y-auto text-left">
                    <div class="mb-2 text-sm font-semibold">
                      {{ files.length }} files selected:
                    </div>
                    <div
                      v-for="(file, index) in files"
                      :key="index"
                      class="mb-1 flex items-center justify-between text-xs"
                    >
                      <span class="max-w-[70%] truncate">{{ file.name }}</span>
                      <div class="flex items-center">
                        <span class="mr-2">
                          {{ getFileSizeString(file.size) }}
                        </span>
                        <Button
                          icon="pi pi-trash"
                          severity="danger"
                          text
                          rounded
                          aria-label="Delete"
                          size="small"
                          :pt="{
                            root: {
                              class:
                                'text-[var(--p-danger-soft-icon)] hover:[background-color:var(--p-danger-soft-bg)] hover:text-[var(--p-danger-soft-text)]',
                            },
                          }"
                          @click="removeFileCallback(index)"
                        />
                      </div>
                    </div>
                  </div>
                </template>

                <div
                  v-if="canPerformScan && quotas"
                  class="mb-4 rounded border p-2 text-sm [background-color:var(--p-info-soft-bg)] [border-color:var(--p-info-soft-border)] [color:var(--p-info-soft-text)]"
                >
                  Current scans: {{ calculateQuotaDisplay(quotas) }}
                </div>

                <div
                  v-if="quotas"
                  class="mb-4 rounded border p-3 text-sm"
                  :class="
                    isStorageFull
                      ? '[background-color:var(--p-danger-soft-bg)] [border-color:var(--p-danger-soft-border)] [color:var(--p-danger-soft-text)]'
                      : storagePercentage >= 80
                        ? '[background-color:var(--p-warning-soft-bg)] [border-color:var(--p-warning-soft-border)] [color:var(--p-warning-soft-text)]'
                        : 'border-purple-200 bg-purple-50 text-purple-700'
                  "
                >
                  <div class="mb-2 font-semibold">
                    Storage: {{ storageDisplay }}
                  </div>

                  <template v-if="isStorageFull">
                    <div class="mb-3 text-xs opacity-90">
                      Storage quota reached. Please delete existing models to
                      free up space, or contact an admin to increase your quota.
                    </div>
                    <div class="flex justify-center gap-2">
                      <StwButton
                        label="My Dashboard"
                        color="gray"
                        size="xs"
                        @click="router.push('/dashboard')"
                      />
                    </div>
                  </template>

                  <div
                    v-else-if="storagePercentage >= 80"
                    class="text-xs opacity-90"
                  >
                    Storage is at {{ storagePercentage }}%. Consider removing
                    unused models, or contact admin to increase your quota.
                  </div>
                </div>

                <Message
                  v-if="isScanRequired && !hasScansRemaining"
                  severity="error"
                  class="mb-4"
                  :closable="false"
                  :pt="{
                    root: {
                      class:
                        '[background-color:var(--p-danger-soft-bg)] border [border-color:var(--p-danger-soft-border)] text-[var(--p-danger-soft-text)]',
                    },
                    icon: { class: 'text-[var(--p-danger-soft-icon)]' },
                    text: { class: 'text-[var(--p-danger-soft-text)]' },
                  }"
                >
                  You have used all your available scans for this month. Please
                  wait for the next month or contact admin for more scans.
                </Message>

                <Message
                  v-if="imageCountTooFew"
                  severity="error"
                  class="mb-4"
                  :closable="false"
                  :pt="{
                    root: {
                      class:
                        '[background-color:var(--p-danger-soft-bg)] border [border-color:var(--p-danger-soft-border)] text-[var(--p-danger-soft-text)]',
                    },
                    icon: { class: 'text-[var(--p-danger-soft-icon)]' },
                    text: { class: 'text-[var(--p-danger-soft-text)]' },
                  }"
                >
                  Number of selected images does not meet the minimum count of
                  20 images.
                </Message>

                <div class="flex justify-center gap-3">
                  <StwButton
                    icon="pi pi-trash"
                    label="Clear"
                    color="red"
                    @click="handleClear"
                  />
                  <StwButton
                    v-if="isAllowAddMoreFiles"
                    icon="pi pi-plus"
                    label="Add More Files"
                    color="blue"
                    @click="handleAddFile"
                  />
                  <StwButton
                    icon="pi pi-upload"
                    label="Upload"
                    color="primary"
                    :disabled="
                      isStorageFull ||
                      (isScanRequired &&
                        (!canPerformScan || isInvalidAspectRatio))
                    "
                    @click="handleUpload"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="uploadedFiles.length > 0"
            class="flex w-full justify-center"
          >
            <div class="w-full max-w-md text-center">
              <div class="!text-surface-950 mb-4 text-lg font-bold">
                Uploaded File{{ uploadedFiles.length > 1 ? 's' : '' }}
              </div>
              <div
                class="bg-surface-50/50 !text-surface-950 rounded-xl p-6 shadow-sm ring-1 ring-black/10 backdrop-blur"
              >
                <template v-if="uploadedFiles.length === 1">
                  <div class="mb-2 break-all text-xl font-semibold">
                    {{ uploadedFiles[0].name }}
                  </div>
                  <div class="mb-2 text-sm opacity-80">
                    Type: {{ getFileTypeString(uploadedFiles[0]) }}
                  </div>
                  <div class="mb-4 text-sm opacity-80">
                    Size: {{ getFileSizeString(uploadedFiles[0].size) }}
                  </div>
                </template>

                <template v-else>
                  <div class="mb-4 max-h-40 overflow-y-auto text-left">
                    <div class="mb-2 text-sm font-semibold">
                      {{ uploadedFiles.length }} files uploaded:
                    </div>
                    <div
                      v-for="(file, index) in uploadedFiles"
                      :key="index"
                      class="mb-1 flex justify-between text-xs"
                    >
                      <span class="max-w-[80%] truncate">{{ file.name }}</span>
                      <span>{{ getFileSizeString(file.size) }}</span>
                    </div>
                  </div>
                </template>

                <Badge
                  value="Uploaded Successfully"
                  severity="success"
                  class="mb-4"
                  :pt="{
                    root: { class: 'bg-green-600 text-white' },
                  }"
                />
                <div class="flex justify-center">
                  <Button
                    icon="pi pi-times"
                    label="Clear"
                    severity="secondary"
                    :pt="{
                      root: {
                        class:
                          'bg-surface-200 border-surface-300 hover:bg-surface-300 hover:border-surface-400',
                      },
                    }"
                    @click="handleClear"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #empty>
        <div
          class="bg-surface-50/40 flex h-full w-full cursor-pointer flex-col items-center justify-center rounded-xl p-8 ring-1 ring-black/10 backdrop-blur"
          @click="handleAddFile"
        >
          <i
            class="pi pi-cloud-upload !text-surface-950 !rounded-full !border-2 !p-8 !text-4xl"
          />
          <p
            class="!text-surface-950 mb-0 mt-6 flex items-center gap-1 opacity-90"
          >
            Click here, drag and drop, or paste files to upload
            <HelpTip help-key="upload" />
          </p>
        </div>
      </template>
    </FileUpload>
  </div>

  <FullscreenSpinner v-if="isLoading" />
</template>

<script setup lang="ts">
import type { QuotasData } from '@stw/types';
import { Badge, Button, FileUpload, Message, useConfirm } from 'primevue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { createItem, deleteItem, updateItem } from '@/api/item';
import {
  getImageFileUploadUrl,
  getImageUploadUrl,
  getVideoUploadUrl,
  startImageScan,
  startVideoScan,
  uploadToSignedUrl as uploadToBucketBySignedUrl,
} from '@/api/upload';
import { getQuotas } from '@/api/user';
import FullscreenSpinner from '@/components/FullscreenSpinner.vue';
import HelpTip from '@/components/HelpTip.vue';
import { StwButton, StwMessage } from '@/components/stw';
import { formatFileSize, formatStorageQuota } from '@/utils/byteFormat';
import { uploadFileInChunks } from '@/utils/chunk';
import { calculateQuotaDisplay } from '@/utils/quotaUtils';
import type { StwFetchError } from '@/utils/stwFetch';
import { useStwToast } from '@/utils/stwToast';

const MAX_DURATION_SECONDS = 180;

const confirm = useConfirm();
const toast = useStwToast();
const router = useRouter();
const fileUploadRef = ref();
const files = ref<File[]>([]);
const isLoading = ref<boolean>(false);
const quotas = ref<QuotasData | null>(null);
const isInvalidAspectRatio = ref(false);

const emit = defineEmits<{
  uploadComplete: [];
}>();

const handleAddFile = () => {
  fileUploadRef.value.choose();
};

const MODEL_EXTENSIONS = ['.glb', '.ply', '.ksplat'] as const;
const ACCEPT_STRING = `${MODEL_EXTENSIONS.join(', ')}, image/*, video/*`;

const checkIsModel = (file: File): boolean => {
  const filename = file.name.toLowerCase();
  return MODEL_EXTENSIONS.some((ext) => filename.endsWith(ext));
};

const checkIsImage = (file: File): boolean => {
  return file.type.startsWith('image/');
};

const checkIsVideo = (file: File): boolean => {
  return file.type.startsWith('video/');
};

const isAllowAddMoreFiles = computed(() => {
  return !files.value.some((file) => {
    return checkIsModel(file) || checkIsVideo(file);
  });
});

const isScanRequired = computed(() => {
  return files.value.some((file) => checkIsVideo(file) || checkIsImage(file));
});

const hasScansRemaining = computed(() => {
  // Allow scan if quota is unlimited (-1) or has available total credits (plan + topup)
  return (
    quotas.value?.scan_quota === -1 ||
    (quotas.value?.total_scan_credit || 0) > 0
  );
});

const canPerformScan = computed(() => {
  return (
    isScanRequired.value &&
    hasScansRemaining.value &&
    !imageCountTooFew.value &&
    !isInvalidAspectRatio.value
  );
});

const imageCount = ref(0);
const imageCountTooFew = computed(() => {
  return imageCount.value !== 0 && imageCount.value < 20;
});

const isStorageFull = computed(() => {
  if (!quotas.value) return false;
  // Block if quota is 0 (no storage) or if used >= quota (when quota > 0)
  // Allow if quota is negative (unlimited)
  return (
    quotas.value.storage_quota === 0 ||
    (quotas.value.storage_quota > 0 &&
      quotas.value.storage_used >= quotas.value.storage_quota)
  );
});

const storageDisplay = computed(() => {
  if (!quotas.value) return 'Loading...';
  return `${formatFileSize(quotas.value.storage_used)} / ${formatStorageQuota(quotas.value.storage_quota)}`;
});

const storagePercentage = computed(() => {
  if (!quotas.value || quotas.value.storage_quota === 0) return 0;
  return Math.min(
    Math.round((quotas.value.storage_used / quotas.value.storage_quota) * 100),
    100
  );
});

const displayDialog = (options: {
  header: string;
  message: string;
  acceptText?: string;
  acceptFunc?: () => any;
  hideReject?: boolean;
  rejectText?: string;
  rejectFunc?: () => any;
}) => {
  confirm.require({
    header: options.header,
    message: options.message,
    icon: 'pi pi-exclamation-circle',
    acceptLabel: options.acceptText ?? 'OK',
    rejectLabel: options.rejectText ?? 'Cancel',
    rejectClass: options.hideReject ? 'hidden' : undefined,
    accept: options.acceptFunc,
    reject: options.rejectFunc,
    // this is assuming closing dialog = reject
    onHide: options.rejectFunc,
  });
};

const handleSelect = async () => {
  const oldFiles = [...files.value];
  const currentFiles = fileUploadRef.value.files;

  const hasModelFiles = currentFiles.some((file: File) => {
    return checkIsModel(file);
  });
  const hasImageFiles = currentFiles.some((file: File) => {
    return checkIsImage(file);
  });
  const hasVideoFiles = currentFiles.some((file: File) => {
    return checkIsVideo(file);
  });

  const hasMixedFiles =
    (hasImageFiles && hasVideoFiles) ||
    (hasModelFiles && (hasImageFiles || hasVideoFiles));

  if (hasMixedFiles) {
    fileUploadRef.value.files = oldFiles;

    toast.add({
      severity: 'error',
      summary: 'Error!',
      detail: 'Cannot upload multiple types of files at the same time.',
    });

    return;
  }

  if (!isAllowAddMoreFiles.value) {
    fileUploadRef.value.files = oldFiles;

    toast.add({
      severity: 'error',
      summary: 'Error!',
      detail: 'Cannot upload more than one file of the selected type at once.',
    });

    return;
  }

  if (hasVideoFiles) {
    const metadata = await getVideoMetadata(currentFiles[0]);

    isInvalidAspectRatio.value = false; // Reset before check

    const landscapeAspectRatio = 16 / 9; // 1.777...
    const portraitAspectRatio = 9 / 16; // 0.5625
    const actualAspectRatio = metadata.aspectRatio;

    const tolerance = 0.02;

    const isValidLandscape =
      Math.abs(actualAspectRatio - landscapeAspectRatio) <= tolerance;
    const isValidPortrait =
      Math.abs(actualAspectRatio - portraitAspectRatio) <= tolerance;

    if (!isValidLandscape && !isValidPortrait) {
      isInvalidAspectRatio.value = true;
      fileUploadRef.value.files = oldFiles; // Keep old files
      displayDialog({
        header: 'Currently not supported.',
        message: `Only 16:9 (landscape or portrait) videos are supported.`,
        acceptText: 'OK',
        hideReject: true,
        acceptFunc: () => {
          // User acknowledged, files are already reverted
        },
        rejectFunc: () => {
          // Should not happen with hideReject: true
        },
      });
      return;
    }

    if (metadata.duration > MAX_DURATION_SECONDS) {
      // Always inform the user that videos longer than 3 minutes will be trimmed
      toast.add({
        severity: 'info',
        summary: 'Video will be trimmed.',
        detail:
          'Only the first 3 minutes of the video will be used to generate the 3D model.',
      });

      files.value = [...fileUploadRef.value.files];
    }
  } else if (hasImageFiles) {
    if (currentFiles.length > 300) {
      fileUploadRef.value.files = oldFiles;
      displayDialog({
        header: 'Too Many Images',
        message: 'Number of selected images exceeded the maximum of 300 files.',
        acceptText: 'OK',
        hideReject: true,
      });
      return;
    } else imageCount.value = currentFiles.length;
  }

  files.value = [...fileUploadRef.value.files];
};

// accept only takes '.ext' tokens and mime patterns ('image/*', 'video/*',
// or an exact type like 'image/png') - matches what PrimeVue's own accept
// validation understands, since pasted files bypass that (they're injected
// directly into fileUploadRef.files rather than going through its normal
// picker/drag-drop input).
const isFileAccepted = (file: File): boolean => {
  const filename = file.name.toLowerCase();

  return ACCEPT_STRING.split(',').some((rawToken) => {
    const token = rawToken.trim();
    if (token.startsWith('.')) return filename.endsWith(token);
    if (token.endsWith('/*')) return file.type.startsWith(token.slice(0, -1));
    return file.type === token;
  });
};

const handlePaste = (event: ClipboardEvent) => {
  if (isLoading.value) return;

  const items = event.clipboardData?.items;
  if (!items) return;

  const pastedFiles = Array.from(items)
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);

  if (pastedFiles.length === 0) return;

  event.preventDefault();

  const acceptedFiles = pastedFiles.filter(isFileAccepted);

  if (acceptedFiles.length === 0) {
    toast.add({
      severity: 'error',
      summary: 'Error!',
      detail: 'Pasted file type is not supported for this upload type.',
    });
    return;
  }

  fileUploadRef.value.files = [...fileUploadRef.value.files, ...acceptedFiles];
  handleSelect();
};

const handleClear = () => {
  files.value.length = 0;
  fileUploadRef.value.clear();
};

const handleUpload = async () => {
  // Check if storage quota already reached
  if (
    quotas.value &&
    (quotas.value.storage_quota === 0 ||
      (quotas.value.storage_quota > 0 &&
        quotas.value.storage_used >= quotas.value.storage_quota))
  ) {
    confirm.require({
      header: 'Storage Limit Reached',
      message:
        'You have reached your storage limit. Please delete existing models to free up space, or contact an admin to increase your quota.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Go to Dashboard',
      accept: () => {
        router.push('/dashboard');
      },
    });

    return;
  }

  let item_id: string | undefined;
  const file: File = files.value[0];
  const ext = file.name.split('.').pop();

  if (!ext) {
    return console.error('File extension not found.');
  }

  isLoading.value = true;

  try {
    if (checkIsModel(file)) {
      const { item_id: createdItemId } = await createItem(ext, 'model');
      item_id = createdItemId;

      // Use client-side chunking for model uploads
      await uploadFileInChunks(createdItemId, fileUploadRef.value.files[0]);

      await updateItem(createdItemId, {
        name: file.name.replace(/\.[^/.]+$/, ''),
      });

      showUploadSuccess(false);
      router.push(`/item/${createdItemId}`);
    } else if (checkIsVideo(file)) {
      const fileName = file.name.replace(/\.[^/.]+$/, '');
      const { item_id: createdItemId } = await createItem(
        'ply',
        'video',
        fileName
      );
      item_id = createdItemId;

      const { path, signedUrl: url } = await getVideoUploadUrl(createdItemId);

      await uploadToBucketBySignedUrl(url, file);

      const metadata = await getVideoMetadata(file);

      const { duration, width, height } = metadata;

      await startVideoScan(createdItemId, duration, width, height, path);

      showUploadSuccess(true);

      router.push(`/dashboard`);
    } else if (checkIsImage(file)) {
      // Handle image batch upload using new refactored API
      const imageFiles = files.value;
      const baseImageName = imageFiles[0].name.replace(/\.[^/.]+$/, '');
      const { item_id: createdItemId } = await createItem(
        'ply',
        'image',
        `${baseImageName} (Image Set)`
      );
      item_id = createdItemId;

      // Get upload URL for images
      const { path } = await getImageUploadUrl(createdItemId);

      // Upload all image files to the batch upload URL
      const uploadPromises = imageFiles.map(async (file, index) => {
        // Generate numbered filename like image-001.jpg, image-002.jpg, etc.
        const paddedIndex = String(index + 1).padStart(3, '0');
        const fileExtension = file.name.split('.').pop();
        const numberedFilename = `image-${paddedIndex}.${fileExtension}`;

        // Get individual signed URL for each file
        const { signedUrl } = await getImageFileUploadUrl(
          createdItemId,
          numberedFilename
        );

        await uploadToBucketBySignedUrl(signedUrl, file);
      });

      await Promise.all(uploadPromises);

      // Start image scan using new API
      await startImageScan(createdItemId, path);

      showUploadSuccess(true);

      router.push(`/dashboard`);
    }

    emit('uploadComplete');
  } catch (error) {
    console.error('Upload failed:', error);

    const fetchError = error as StwFetchError;

    // Check for storage quota error from backend
    if (fetchError.message?.includes('Storage quota exceeded')) {
      confirm.require({
        header: 'Storage Limit Reached',
        message:
          'You have reached your storage limit. Please delete existing models to free up space, or contact an admin to increase your quota.',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Go to Dashboard',
        accept: () => {
          router.push('/dashboard');
        },
      });
    } else {
      // Generic error toast
      toast.add({
        severity: 'error',
        summary: 'Error!',
        detail: 'File not uploaded. Error: ' + fetchError.message,
      });
    }

    if (item_id) {
      await deleteItem(item_id);
    }
  } finally {
    isLoading.value = false;
  }
};

const fetchUserQuotas = async () => {
  try {
    quotas.value = await getQuotas();
  } catch (error) {
    console.error('Failed to fetch quotas:', error);
    toast.add({
      severity: 'error',
      summary: 'Error!',
      detail: 'Could not fetch user scan quotas.',
    });
  }
};

const getFileTypeString = (file: File): string => {
  const filename = file.name.toLowerCase();

  if (filename.endsWith('.glb')) {
    return '3D Model (.GLB)';
  }

  if (filename.endsWith('.ply')) {
    return '3D Model (.PLY)';
  }

  if (filename.endsWith('.ksplat')) {
    return 'Compressed 3D Model (.KSPLAT)';
  }

  const mimeType = file.type;

  if (mimeType.startsWith('image/')) {
    return `${mimeType.split('/')[1].toUpperCase()} Image`;
  }

  if (mimeType.startsWith('video/')) {
    return `${mimeType.split('/')[1].toUpperCase()} Video`;
  }

  const ext = filename.split('.').pop();
  return `${ext?.toUpperCase() || 'Unknown'} file`;
};

const getFileSizeString = (bytes: number): string => {
  if (bytes === 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Format the number with max 2 decimals
  const formattedSize = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(bytes / Math.pow(k, i));

  return `${formattedSize} ${units[i]}`;
};

const showUploadSuccess = (isProcessing = true) => {
  toast.add({
    severity: 'success',
    summary: 'Upload success.',
    detail: isProcessing
      ? 'Your upload is processing. This may take ~20–40 minutes.'
      : 'Your upload was successful.',
  });
};

const getVideoMetadata = (
  file: File
): Promise<{
  duration: number;
  width: number;
  height: number;
  aspectRatio: number;
}> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        aspectRatio: video.videoWidth / video.videoHeight,
      });
    };

    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject('Error loading video metadata.');
    };

    video.src = URL.createObjectURL(file);
  });
};

watch(
  isScanRequired,
  (newVal) => {
    if (newVal && !quotas.value) {
      fetchUserQuotas();
    }
  },
  { immediate: true }
);

onMounted(() => {
  // Always fetch quotas on mount to show storage quota for all upload types
  fetchUserQuotas();

  // Window-level rather than on the dropzone itself: this component is the
  // whole upload dialog's content while it's open, and PrimeVue's dropzone
  // doesn't receive focus/paste targeting on its own.
  window.addEventListener('paste', handlePaste);
});

onUnmounted(() => {
  window.removeEventListener('paste', handlePaste);
});
</script>

<style scoped>
/* Ensure empty container and its content take full height */
:deep(.p-fileupload-content) {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
}

:deep(.p-fileupload-content [data-pc-section='empty']) {
  display: flex;
  flex: 1;
  width: 100%;
  min-height: 0;
  align-items: center;
  justify-content: center;
}

.upload-empty-area {
  height: 100%;
  min-height: 100%;
}
</style>
